import { useTheme } from '@emotion/react';
import { useState, useEffect } from 'react';
import { useMedia } from '@packlink/utils';
import { IconNames } from '@shipengine/giger-theme';
import { Icon } from '@shipengine/giger';
import { DropOff } from '@packlink/cartography';
import { getServiceCardStyles, getServiceCardBodyStyles, getTransitLogoWrapperStyles, getBodyInfoWrapperStyles, getBodyInfoStyles, getDeliveryContainerStyles, getServiceCardArrowSectionStyles, getServiceCardFooterStyles, getTagsWrapperStyles } from './ServiceCardStyles.js';
import { ServiceTagType } from '@packlink/packlink-sdk';
import { jsxs, jsx } from '@emotion/react/jsx-runtime';
import { ServiceNotification } from './ServiceNotification/ServiceNotification.js';
import { Transit } from './Transit/Transit.js';
import { Carrier } from './Carrier/Carrier.js';
import { Collection } from './Route/Collection/Collection.js';
import { Delivery } from './Route/Delivery/Delivery.js';
import { Price } from './Price/Price.js';
import { PriceAnatomy } from './PriceAnatomy/PriceAnatomy.js';
import { ServiceInfo } from './ServiceInfo/ServiceInfo.js';
import { DetailsButton } from './DetailsButton/DetailsButton.js';
import { ServiceCardDialog, ServiceCardDialogType } from './ServiceCardDialog/ServiceCardDialog.js';
import { ServiceInformation } from './ServiceInformation/ServiceInformation.js';
import { SERVICES_TAG_OPTIONS, ServiceTag } from './ServiceTag/ServiceTag.js';

const ServiceCard = _ref => {
  let {
    isOwnContract,
    serviceNotification,
    logo,
    details,
    dropOffPoints,
    serviceInfo,
    transit,
    serviceName,
    fallbackLogo,
    from,
    timeLeftToBook,
    to,
    price,
    appliedTax,
    markerIcon,
    priceAnatomyItems,
    products,
    className: propClassName,
    onSelect,
    onDetailsRequest,
    onDropOffPointsRequest,
    tags = [],
    tagsFeatureEnabled = false,
    taxPrice
  } = _ref;
  const [detailsState, setDetails] = useState(details);
  const [fetchingDetails, setFetchingDetails] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(null);
  const [dropOffPointsState, setDropOffPoints] = useState(dropOffPoints || undefined);
  const theme = useTheme();
  const isMobile = useMedia(`(max-width: ${theme.breakPoints.tablet}px)`);
  const showTags = tagsFeatureEnabled && tags.length > 0;
  const hasTaggedServiceBackground = showTags && tags.some(tag => tag.id !== ServiceTagType.ECO_FRIENDLY && tag.id !== ServiceTagType.EXCLUSIVE_FOR_PLUS);
  useEffect(() => setDetails(details), [details]);
  useEffect(() => setDropOffPoints(dropOffPoints || []), [dropOffPoints]);
  const toggleDialog = type => setDialogOpen(dialogOpen === type || !type ? null : type);
  const handleToggleDialog = () => toggleDialog();
  const handleToggleDropOffPoints = e => {
    e === null || e === void 0 ? void 0 : e.preventDefault();
    if (!dropOffPoints && onDropOffPointsRequest) {
      onDropOffPointsRequest().then(data => {
        setDropOffPoints(data);
        toggleDialog(ServiceCardDialogType.DROPOFF);
      });
    } else {
      toggleDialog(ServiceCardDialogType.DROPOFF);
    }
  };
  const handleToggleDetails = e => {
    e === null || e === void 0 ? void 0 : e.preventDefault();
    if (!details && onDetailsRequest) {
      setFetchingDetails(true);
      onDetailsRequest().then(data => {
        setDetails(data);
        setFetchingDetails(false);
        toggleDialog(ServiceCardDialogType.DETAILS);
      });
    } else {
      toggleDialog(ServiceCardDialogType.DETAILS);
    }
  };
  const renderDropOff = () => {
    if (!window.google || !dropOffPointsState) {
      return;
    }
    return jsx(DropOff, {
      addresses: dropOffPointsState,
      markerIcon: markerIcon
    });
  };
  const TagsSection = () => {
    return jsx("div", {
      css: getTagsWrapperStyles(hasTaggedServiceBackground),
      children: tags.sort((a, b) => a.id.localeCompare(b.id)).filter(_ref2 => {
        let {
          id
        } = _ref2;
        return !!SERVICES_TAG_OPTIONS[id];
      }).map(_ref3 => {
        let {
          id
        } = _ref3;
        return jsx(ServiceTag, {
          ...SERVICES_TAG_OPTIONS[id]
        }, id);
      })
    });
  };
  return jsxs("article", {
    className: propClassName,
    "data-id": `service-${serviceName}`,
    children: [serviceNotification && jsx(ServiceNotification, {
      ...serviceNotification
    }), jsxs("div", {
      css: getServiceCardStyles,
      children: [isMobile && showTags && jsx(TagsSection, {}), jsxs("section", {
        css: getServiceCardBodyStyles(showTags, hasTaggedServiceBackground),
        children: [jsxs("div", {
          css: getTransitLogoWrapperStyles,
          children: [jsx(Transit, {
            transit: transit,
            showTags: showTags
          }), jsx(Carrier, {
            carrierImageAlt: serviceName,
            carrierlogo: logo,
            carrierFallbackLogo: fallbackLogo,
            serviceName: serviceName,
            isOwnContract: isOwnContract
          })]
        }), jsxs("div", {
          css: getBodyInfoWrapperStyles,
          children: [!isMobile && showTags && jsx(TagsSection, {}), jsxs("div", {
            css: getBodyInfoStyles,
            children: [jsxs("div", {
              css: getDeliveryContainerStyles,
              children: [jsx(Collection, {
                collection: from,
                timeLeftToBook: timeLeftToBook,
                onToggleDropOffPoints: handleToggleDropOffPoints
              }), jsx("section", {
                css: getServiceCardArrowSectionStyles,
                children: jsx(Icon, {
                  name: IconNames.CHEVRON_BOTTOM
                })
              }), jsx(Delivery, {
                delivery: to
              })]
            }), jsx(Price, {
              products: products,
              price: price,
              appliedTax: appliedTax,
              onPriceSelect: onSelect,
              taxPrice: taxPrice
            })]
          })]
        })]
      }), priceAnatomyItems && jsx(PriceAnatomy, {
        items: priceAnatomyItems
      }), jsxs("footer", {
        css: getServiceCardFooterStyles,
        children: [jsx(ServiceInfo, {
          serviceInfo: serviceInfo
        }), jsx(DetailsButton, {
          detailsNode: detailsState,
          dialogOpen: dialogOpen,
          onToggleDetails: handleToggleDetails,
          fetchingDetails: fetchingDetails
        })]
      })]
    }), isMobile ? jsx(ServiceCardDialog, {
      detailsNode: detailsState,
      dialogOpen: dialogOpen,
      dropOffNode: renderDropOff(),
      onToggleDialog: handleToggleDialog
    }) : dialogOpen && jsx(ServiceInformation, {
      dialogOpen: dialogOpen,
      onToggleDialog: handleToggleDialog,
      detailsNode: detailsState,
      dropOffNode: renderDropOff()
    })]
  });
};

export { ServiceCard };
//# sourceMappingURL=ServiceCard.js.map
